/* eslint-disable quotes */
import { Form, Modal, Input } from 'antd';
import { useState } from 'react';
import { hasValidCharacterTypes } from '../../utils/password';
import { resetPassword } from '../../../api';
import { messageError, messageSuccess } from '../../../components/message';
import { md5 } from '../../../lib';
import { AppLoginPlatform } from '../../../model';

interface IProps {
  readonly sourcePlatform?: AppLoginPlatform;
  readonly showResetPasswordModal: boolean;
  readonly onCancel: () => void;
  readonly email: string;
}

export const ResetPasswordModal = (props: IProps) => {
  const { sourcePlatform } = props;
  const [resetPasswordForm] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);

  const handleConfirm = async () => {
    try {
      const values = await resetPasswordForm.validateFields();
      if (values) {
        setConfirmLoading(true);
        const res = await resetPassword({
          email: values.email,
          password: md5(values.newPassword),
          sourcePlatform,
        });
        if (res === 0) {
          messageSuccess(`${props.email} password reset succeeded.`);
          onCancel();
        } else if (res.msg) {
          messageError(res.msg);
        } else {
          messageError(`${props.email} password reset failed.`);
        }
      }
    } catch (err: any) {
      messageError(`${props.email} password reset failed.`);
      console.error(err);
    } finally {
      setConfirmLoading(false);
    }
  };

  const validateEmail = (_: any, value: string) => {
    if (!value) {
      return Promise.reject('输入邮箱地址');
    }
    if (!/^[\S]{1,30}@[\S]{1,30}$/.test(value)) {
      return Promise.reject(
        "您输入的电子邮件格式似乎不正确。请确保您的电子邮件中包含“@”符号和域名。例如，“example@example.com'"
      );
    }
    if (value !== props.email) {
      return Promise.reject('请确认邮箱地址和选择的用户是否一致');
    }
    return Promise.resolve();
  };

  const validatePassword = (_: any, value: string) => {
    const values = resetPasswordForm.getFieldsValue();
    if (value.trim().length < 10) {
      return Promise.reject('最少10个字符');
    }
    if (value.trim().length > 72) {
      return Promise.reject('最多72个字符');
    }
    if (value.includes(' ')) {
      return Promise.reject('没有空格');
    }
    if (!hasValidCharacterTypes(value)) {
      return Promise.reject(
        '请选择一个更强的密码。尝试混合使用字母、数字和符号。'
      );
    }
    if (
      _.field === 'confirmPassword' &&
      values.newPassword !== values.confirmPassword
    ) {
      return Promise.reject('您输入的密码不匹配。请确保输入相同的密码。');
    }
    return Promise.resolve();
  };

  const onCancel = () => {
    props.onCancel();
    setConfirmLoading(false);
    resetPasswordForm.setFieldValue('email', '');
    resetPasswordForm.setFieldValue('newPassword', '');
    resetPasswordForm.setFieldValue('confirmPassword', '');
  };

  return (
    <Modal
      title="重置密码"
      open={props.showResetPasswordModal}
      onOk={handleConfirm}
      onCancel={onCancel}
      okText="确认"
      confirmLoading={confirmLoading}
      className="reset-password-modal"
      maskClosable={false}
    >
      <div>
        <p>
          是否确认重置 <span style={{ color: '#CB2431' }}>{props.email}</span>{' '}
          用户的密码? 请确认输入此用户的电子邮箱地址验证
        </p>
      </div>
      <Form form={resetPasswordForm} colon={false}>
        <Form.Item
          name="email"
          label="邮箱地址"
          className="list-item"
          validateTrigger={['onBlur']}
          rules={[
            {
              validator: validateEmail,
            },
          ]}
        >
          <Input placeholder="请输入用户电子邮箱地址" />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label="新密码"
          className="list-item"
          validateTrigger={['onBlur']}
          rules={[
            {
              validator: validatePassword,
            },
          ]}
        >
          <Input.Password placeholder="请输入用户密码" />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="确认密码"
          className="list-item"
          validateTrigger={['onBlur']}
          rules={[
            {
              validator: validatePassword,
            },
          ]}
        >
          <Input.Password placeholder="请再次输入用户密码" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
